import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Avatar, Button, Tag, Breadcrumb, Spin, Input, Upload } from 'antd';
import {
  ShareAltOutlined,
  EditOutlined,
  LeftOutlined,
} from '@ant-design/icons';
import userService from '../services/userService';
import { getCurrentUser, setCurrentUser } from '../services/authService';
import assetService from '../services/assetService';
import CreateTemplateModal from '../components/modals/CreateTemplateModal';
import PersonalTab from '../components/tab/PersonalTab';
import useAppMessage from '../hooks/useAppMessage';
import bg from '../assets/bg.png';
import EditTagsModal from '../components/modals/EditTagsModal';
import SharePopover from '../components/common/SharePopover';
import { SHARE_SCENE_TYPE, SHARE_QR_CONFIG } from '../utils/constants';
import { useParams, useNavigate } from 'react-router-dom';
import SocialDrawer from '../components/social/SocialDrawer';
import { handleCopy } from '../utils/common';

import certificateIcon from '../styles/images/icon_certificate.svg';
import aiAgentIcon from '../styles/images/icon_ai_agent.svg';
import watermarkCertificate from '../styles/images/watermark_certificate.svg';
import watermarkAiAgent from '../styles/images/watermark_ai_agent.svg';
import maskRoundedSlant from '../assets/mask-rounded-slant.svg';
import ClipRoundedSlantDefs from '../assets/ClipRoundedSlantDefs';

const PersonalInfoPage = () => {
  const { id: routeId } = useParams();
  const navigate = useNavigate();
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [posts, setPosts] = useState([]);
  // 新增：社交媒体数据
  const [socialLinks, setSocialLinks] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [editNameAvatar, setEditNameAvatar] = useState(false);
  const [editBio, setEditBio] = useState(false);
  const [editTagsModalVisible, setEditTagsModalVisible] = useState(false);
  const [editName, setEditName] = useState('');
  const [editAvatar, setEditAvatar] = useState('');
  const [editAvatarFile, setEditAvatarFile] = useState(null);
  const [editAvatarPreview, setEditAvatarPreview] = useState('');
  const [editBioValue, setEditBioValue] = useState('');
  const [editTagsInit, setEditTagsInit] = useState({});
  const [socialDrawerVisible, setSocialDrawerVisible] = useState(false);
  const [assetStats, setAssetStats] = useState({ certificates: 0, computing: 0 });
  const { message, contextHolder } = useAppMessage();

  // 使用 useRef 避免 message 依赖导致的循环渲染
  const stableMessage = useRef(message);
  useEffect(() => {
    stableMessage.current = message;
  });

  const currentUserId = useMemo(() => getCurrentUser()?.id, []);
  const isSelf = useMemo(() => {
    // 只在组件初始加载时计算，避免因userInfo更新导致的循环依赖
    return currentUserId && routeId && currentUserId === routeId;
  }, [currentUserId, routeId]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      if (!routeId) {
        stableMessage.current.error('未指定用户ID');
        setLoading(false);
        return;
      }
      const userInfoData = await userService.getUserDetail(routeId);
      setUserInfo(userInfoData);
    } catch (e) {
      console.error('获取个人信息失败:', e);
      stableMessage.current.error('获取个人信息失败');
    } finally {
      setLoading(false);
    }
  }, [routeId]);

  // 获取该用户的帖子
  const fetchPosts = useCallback(async () => {
    setPostsLoading(true);
    try {
      if (!routeId) {
        setPosts([]);
        setSocialLinks([]);
        setPostsLoading(false);
        return;
      }

      // 并行请求两个接口，任意一个失败也能继续
      const [res, links] = await Promise.all([
        userService
          .getUserPosts({
            scope: 'personal',
            user_id: routeId,
          })
          .catch((e) => {
            console.error('fetchPosts-getUserPosts-error', e);
            stableMessage.current.error('获取个人主页帖子失败');
            return { cards: [] }; // 返回空数据，保证后续流程
          }),
        userService
          .getUserSocialLinks(routeId)
          .catch((e) => {
            console.error('fetchPosts-getUserSocialLinks-error', e);
            stableMessage.current.error('获取社交媒体链接失败');
            return []; // 返回空数组，保证后续流程
          }),
      ]);

      setPosts(res.cards || []);
      setSocialLinks(links || []);
    } catch (e) {
      // 理论上不会进到这里，除非Promise.all本身出错
      stableMessage.current.error('获取个人主页数据失败');
    } finally {
      setPostsLoading(false);
    }
  }, [routeId]);

  // 获取资产统计数据
  const fetchAssetStats = useCallback(async () => {
    try {
      if (!routeId) return;
      const stats = await assetService.getAssetStats(routeId);
      setAssetStats(stats);
    } catch (error) {
      console.error('获取资产统计失败:', error);
      // 静默失败，不影响页面显示
    }
  }, [routeId]);

  useEffect(() => {
    fetchData();
    fetchPosts();
    fetchAssetStats();
  }, [fetchData, fetchPosts, fetchAssetStats]);

  // 头像/用户名编辑保存
  const handleSaveNameAvatar = async () => {
    setLoading(true);
    try {
      const userId = userInfo?.id;
      let avatarUrl = userInfo.avatar_url;
      // 如果有新头像文件，先上传
      if (editAvatarFile) {
        try {
          const { avatar_url } = await userService.uploadAvatar(
            userId,
            editAvatarFile
          );
          avatarUrl = avatar_url;
        } catch (e) {
          if (e?.error_code === 4029) {
            stableMessage.current.error('头像上传过于频繁，请稍后再试');
          } else if (e?.message) {
            stableMessage.current.error(e.message);
          } else {
            stableMessage.current.error('头像上传失败');
          }
          setLoading(false);
          return;
        }
      }
      await userService.updateUserInfo(userId, {
        username: editName,
        avatar_url: avatarUrl,
        source: 'synnovator',
      });
      setUserInfo(prevUserInfo => ({ ...prevUserInfo, username: editName, avatar_url: avatarUrl }));
      // 同步更新 localStorage
      const currentUser = getCurrentUser();
      setCurrentUser({ ...currentUser, username: editName, avatar_url: avatarUrl });
      setEditNameAvatar(false);
      setEditAvatarFile(null);
      setEditAvatarPreview('');
      stableMessage.current.success('保存成功');
    } catch (err) {
      // 使用后端返回的具体错误信息（如“用户名已被占用”），无则回退到通用提示
      const msg = err?.message || '保存失败';
      stableMessage.current.error(msg);
    } finally {
      setLoading(false);
    }
  };

  // 简介编辑保存
  const handleSaveBio = async () => {
    try {
      const userId = userInfo?.id;
      await userService.updateUserInfo(userId, {
        bio: editBioValue,
        source: 'synnovator',
      });
      setUserInfo(prevUserInfo => ({ ...prevUserInfo, bio: editBioValue }));
      // 同步更新 localStorage
      const currentUser = getCurrentUser();
      setCurrentUser({ ...currentUser, bio: editBioValue });
      setEditBio(false);
      stableMessage.current.success('保存成功');
    } catch (err) {
      const msg = err?.message || '保存失败';
      stableMessage.current.error(msg);
    }
  };

  // 标签编辑保存
  const handleSaveTags = async (values) => {
    try {
      setUserInfo(prevUserInfo => ({
        ...prevUserInfo,
        education: values.education || [],
        preferences: values.preferences || {},
      }));
      // 同步更新 localStorage
      const currentUser = getCurrentUser();
      setCurrentUser({
        ...currentUser,
        education: values.education || [],
        preferences: values.preferences || {},
      });
      setEditTagsModalVisible(false);
      stableMessage.current.success('保存成功');
    } catch {
      stableMessage.current.error('保存失败');
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto py-12 min-h-screen" style={{ backgroundColor: 'var(--color-bg-chat)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="max-w-4xl mx-auto py-12 min-h-screen" style={{ backgroundColor: 'var(--color-bg-chat)' }}>
        未获取到个人信息
      </div>
    );
  }

  // 构建荣誉数据数组
  const honors = [
    {
      type: 'certificate',
      label: '证书',
      count: `${assetStats.certificates} 张证书`,
      bgColor: '#74FFBB',
      titleColor: 'assets-stat-title-active',
      countColor: 'assets-stat-value',
      icon: certificateIcon,
      watermark: watermarkCertificate,
      maskDataUrl: maskRoundedSlant,
      onClick: () => navigate(`/assets?userId=${routeId}&tab=certificates`),
    },
    {
      type: 'agent',
      label: 'Ai/Agent',
      count: `${assetStats.computing} 条`,
      bgColor: '#41FAF4',
      titleColor: 'assets-stat-title-active',
      countColor: 'assets-stat-value',
      icon: aiAgentIcon,
      watermark: watermarkAiAgent,
      maskDataUrl: maskRoundedSlant,
      onClick: () => navigate(`/assets?userId=${routeId}&tab=computing`),
    },
    // {
    //   type: 'reputation',
    //   label: '声望',
    //   count: `${userInfo.statistics?.reputation_score || 0}分`,
    //   bgColor: 'bg-[#f197bd]',
    //   textColor: 'text-[#05042e]',
    //   icon: null,
    // },
    // {
    //   type: 'posts',
    //   label: '帖子',
    //   count: `${userInfo.statistics?.posts_count || 0}个帖子`,
    //   bgColor: 'bg-[#bbf2a8]',
    //   textColor: 'text-[#05042e]',
    //   icon: null,
    // },
    // {
    //   type: 'views',
    //   label: '浏览量',
    //   count: `${userInfo.statistics?.views_count || 0}次浏览`,
    //   bgColor: 'bg-[#e6f67c]',
    //   textColor: 'text-[#05042e]',
    //   icon: null,
    // },
  ];

  return (
    <div
      className="w-full mx-auto min-h-screen relative bg-no-repeat bg-cover"
      style={{ backgroundImage: `url(${bg})` }}
    >
      {contextHolder}
      <div className="max-w-4xl mx-auto py-12 min-h-screen relative">

        {/* 面包屑+返回 */}
        <div className="mb-6 text-lg font-semibold text-[#18371D] tracking-wide flex items-center gap-2">
          <Button
            type="text"
            icon={<LeftOutlined />}
            className="!p-0 mr-2 text-base"
            onClick={() => window.history.back()}
          />
          <Breadcrumb
            items={[
              {
                title: '个人主页'
              }
            ]}
          />
        </div>
        <div className="flex justify-between items-end">
          <div>
            {/* 头像区域 */}
            <div className="flex flex-col items-start mb-6">
              {editNameAvatar ? (
                <>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <Upload
                      accept="image/jpeg,image/png,image/webp"
                      showUploadList={false}
                      beforeUpload={(file) => {
                        // 前端校验格式和大小
                        const isImage =
                          file.type === 'image/jpeg' ||
                          file.type === 'image/png' ||
                          file.type === 'image/webp';
                        if (!isImage) {
                          stableMessage.current.error('仅支持JPEG、PNG、WebP格式头像');
                          return Upload.LIST_IGNORE;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          stableMessage.current.error('头像不能超过5MB');
                          return Upload.LIST_IGNORE;
                        }
                        // 预览
                        const reader = new FileReader();
                        reader.onload = (e) =>
                          setEditAvatarPreview(e.target.result);
                        reader.readAsDataURL(file);
                        setEditAvatarFile(file);
                        return false; // 阻止自动上传
                      }}
                    >
                      <Avatar
                        size={80}
                        src={editAvatarPreview || editAvatar || userInfo.avatar_url || userInfo.avatar}
                        className="bg-blue-100 border-2 border-white shadow-lg text-3xl mb-3"
                        style={{
                          width: 80,
                          height: 80,
                          borderRadius: 20,
                          objectFit: 'cover',
                          cursor: 'pointer',
                        }}
                      >
                        { userInfo.username?.charAt(0)?.toUpperCase()}
                      </Avatar>
                    </Upload>
                    <Input
                      style={{ width: 140, marginRight: 8 }}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      maxLength={12}
                      placeholder="用户名"
                    />
                    <Button
                      type="primary"
                      size="small"
                      onClick={handleSaveNameAvatar}
                      style={{ marginRight: 8 }}
                    >
                      保存
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setEditNameAvatar(false);
                        setEditAvatarFile(null);
                        setEditAvatarPreview('');
                      }}
                    >
                      取消
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <Avatar
                    size={80}
                    src={userInfo.avatar_url || userInfo.avatar}
                    className="bg-blue-100 border-2 border-white shadow-lg text-3xl mb-3"
                    style={{
                      width: 80,
                      height: 80,
                      borderRadius: 20,
                      objectFit: 'cover',
                    }}
                  >
                    {userInfo.username?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg font-medium text-white">
                      { userInfo.username}
                    </span>
                    {isSelf && (
                      <Button
                        type="text"
                        icon={<EditOutlined />}
                        className="!p-0 text-gray-500"
                        onClick={() => {
                          setEditName( userInfo.username);
                          setEditAvatar(userInfo.avatar_url || userInfo.avatar);
                          setEditNameAvatar(true);
                        }}
                      />
                    )}
                  </div>
                </>
              )}
              <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-300">ID: {userInfo.id}</span>
                  <span className="text-xs text-gray-300 cursor-pointer" onClick={() => handleCopy(userInfo.id, message)}>
                    copy
                  </span>
                </div>
         
            </div>
       
              
            {/* 统计数据 */}
            <div className="flex justify-start gap-8 mb-6">
              {/* <div className="text-center">
                <div className="text-lg font-bold text-white">
                  {userInfo.statistics?.following_count || 0}
                </div>
                <div className="text-sm text-gray-500">关注</div>
              </div> */}
              <div className="text-center">
                <div className="text-lg font-bold text-white">
                  {userInfo.statistics?.followers_count || 0}
                </div>
                <div className="text-sm text-gray-500">好友</div>
              </div> 
              <div className="text-center">
                <div className="text-lg font-bold text-white">
                  {userInfo.like_count || 0}
                </div>
                <div className="text-sm text-gray-500">获赞</div>
              </div>
            </div>

            {/* 简介 */}
            <div className=" text-white text-sm mb-4 flex items-center">
              {isSelf && editBio ? (
                <>
                  <Input.TextArea
                    style={{ width: 260, marginRight: 8 }}
                    value={editBioValue}
                    onChange={(e) => setEditBioValue(e.target.value)}
                    maxLength={60}
                    autoSize={{ minRows: 1, maxRows: 3 }}
                  />
                  <Button
                    type="primary"
                    size="small"
                    onClick={handleSaveBio}
                    style={{ marginRight: 8 }}
                  >
                    保存
                  </Button>
                  <Button size="small" onClick={() => setEditBio(false)}>
                    取消
                  </Button>
                </>
              ) : (
                <>
                  <span>{userInfo.bio || '这个人很懒，什么都没有留下'}</span>
                  {isSelf && (
                    <Button
                      type="text"
                      icon={<EditOutlined />}
                      className="!p-0 text-gray-500"
                      onClick={() => {
                        setEditBioValue(userInfo.bio);
                        setEditBio(true);
                      }}
                    />
                  )}
                </>
              )}
            </div>

            {/* 标签 */}
            <div className="flex items-center gap-2 mb-6 flex-wrap">
              {/* 显示预期职业 */}
              {userInfo.preferences?.expected_career && (
                <Tag className="rounded-full bg-[#79C74F] text-white px-3 py-1 border text-xs">
                  {userInfo.preferences.expected_career}
                </Tag>
              )}
              {/* 显示预期场景 */}
              {userInfo.preferences?.expected_scenario && (
                <Tag className="rounded-full bg-[#D9ADFF] text-white px-3 py-1 border text-xs">
                  {userInfo.preferences.expected_scenario}
                </Tag>
              )}
              
              {/* 显示专业/学科 */}
              {userInfo.education && userInfo.education.length > 0 && userInfo.education[0]?.major && (
                <Tag className="rounded-full bg-[#79C74F] text-white px-3 py-1 border text-xs">
                  {userInfo.education[0].major}
                </Tag>
              )}
                  
              {/* 显示教育背景 */}
              {userInfo.education && userInfo.education.length > 0 &&
                userInfo.education.map((edu, index) => {
                  // 判断每个字段是否有值
                  const institution = edu.institution ? edu.institution : '';
                  const field = edu.field ? `- ${edu.field}` : '';
                  const degree = edu.degree ? `- ${edu.degree}` : '';
                  // 如果所有字段都为空，则不展示该项
                  if (!institution && !field && !degree) {
                    return null;
                  }
                  return (
                    <Tag key={`edu-${index}`} className="rounded-full bg-[var(--color-assist-2)] px-3 py-1 border text-xs">
                      {institution}{field}{degree}
                    </Tag>
                  );
                })
              }
              
              {/* 如果没有任何标签，显示默认信息 */}
              {!userInfo.preferences?.expected_career &&
               !userInfo.preferences?.expected_scenario &&
               (!userInfo.education || userInfo.education.length === 0) && (
                <Tag className="rounded-full bg-[var(--color-assist-2)] px-3 py-1 border text-xs">
                  这个人很懒，什么都没有留下
                </Tag>
              )}
              
              {isSelf && (
                <Button
                  type="text"
                  icon={<EditOutlined />}
                  className="!p-0 text-gray-500"
                  onClick={() => {
                    setEditTagsInit({
                      education: userInfo.education || [],
                      preferences: userInfo.preferences || {},
                    });
                    setEditTagsModalVisible(true);
                  }}
                />
              )}
            </div>
          </div>

          {/* 分享按钮和设置按钮 */}
          <div className="flex justify-center items-center gap-4 mb-6">
            {/* {isSelf ? (
              <SharePopover
                message={message}
                userInfo={userInfo}
                basicUserInfo={userInfo}
                qrConfig={SHARE_QR_CONFIG[SHARE_SCENE_TYPE.PERSONAL]}
              >
                <button
                  className="bg-purple-500 hover:bg-purple-600 text-white rounded-full px-20 py-2 text-sm font-medium border-none"
                  icon={<ShareAltOutlined />}
                >
                  分享
                </button>
              </SharePopover>
            ) : (
              <button
                className="bg-purple-500 hover:bg-purple-600 text-white rounded-full px-20 py-2 text-sm font-medium border-none"
              >
                加好友
              </button>
            )} */}
            <SharePopover
              message={message}
              userInfo={userInfo}
              basicUserInfo={userInfo}
              qrConfig={SHARE_QR_CONFIG[SHARE_SCENE_TYPE.PERSONAL]}
              tooltipProps={{
                styles: {
                  body: { padding: '8px 12px' }
                }
              }}
            >
              <button
                className="bg-[var(--color-assist-3)] text-black rounded-full px-20 py-2 text-sm font-medium border-none"
                icon={<ShareAltOutlined />}
              >
                分享
              </button>
            </SharePopover>
          </div>
        </div>
        {/* 荣誉区 */}
        <div className="flex gap-4 mt-6 mb-8">
          <ClipRoundedSlantDefs />
          {honors.map((h) => (
            <div
              key={h.type}
              onClick={h.onClick || undefined}
              className={`relative flex flex-col justify-between p-5 w-[196px] h-[108px] overflow-hidden shadow-sm ${h.onClick ? 'cursor-pointer hover:opacity-90 transition-opacity' : ''}`}
              style={{
                backgroundColor: h.bgColor,
                '--mask-image': `url(${h.maskDataUrl})`,
                WebkitMaskImage: 'var(--mask-image)',
                maskImage: 'var(--mask-image)',
                WebkitMaskSize: '100% 100%',
                maskSize: '100% 100%',
                WebkitMaskRepeat: 'no-repeat',
                maskRepeat: 'no-repeat',
                WebkitMaskPosition: '0 0',
                maskPosition: '0 0',
                WebkitClipPath: 'url(#clipRoundedSlant)',
                clipPath: 'url(#clipRoundedSlant)',
                borderRadius: '15px'
              }}
            >
              {/* Watermark */}
              <img 
                src={h.watermark} 
                alt="watermark" 
                className="absolute z-0 w-[180px] h-[180px] opacity-85"
                style={{ right: '-30px', top: '-20px' }}
              />
              
              {/* Content */}
              <div className="flex items-center justify-between h-full relative z-10">
                <div className="flex flex-col justify-center h-full items-start space-y-2 -ml-3">
                   <span className={h.titleColor}>{h.label}</span>
                   <span className={h.countColor}>{h.count}</span>
                </div>
                
                {/* Icon */}
                <div className="flex items-center justify-center">
                   <img 
                     src={h.icon} 
                     alt="icon" 
                     className="object-contain transition-all duration-300 w-[62px] h-[62px]"
                   />
                </div>
              </div>
            </div>
          ))}
        </div>
        {/* Tab区 */}
        <PersonalTab
          posts={posts}
          socialLinks={socialLinks}
          postsLoading={postsLoading}
          userId={routeId}
          isSelf={isSelf}
          onShowCreateModal={() => setShowCreateModal(true)}
          onShowSocialDrawer={() => setSocialDrawerVisible(true)}
          onRefreshSocialLinks={fetchPosts}
        />
        <CreateTemplateModal
          visible={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          onSubmit={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            fetchPosts(); // 创建成功后刷新帖子
          }}
          type="biography"
        />
        <EditTagsModal
          visible={editTagsModalVisible}
          onCancel={() => setEditTagsModalVisible(false)}
          onSave={handleSaveTags}
          initValues={editTagsInit}
        />
        <SocialDrawer
          visible={socialDrawerVisible}
          onClose={() => setSocialDrawerVisible(false)}
          onSuccess={() => {
            // 刷新社交链接数据
            fetchPosts();
          }}
        />
      </div>
    </div>
  );
};

export default PersonalInfoPage;
